import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useState, type JSX } from "react";

import { ottoBotsApi } from "../api.js";
import type { OttoBot, OttoBotWithSecret } from "../types.js";

/**
 * Create-a-bot form dialog — slug / display name / persona / host origins.
 * On success it hands the freshly-minted bot (with its one-time secret) to
 * `onCreated`. Extracted from `BotsPanel.tsx`.
 */
export function CreateBotDialog({
  onClose,
  onCreated,
  authHeader,
}: {
  onClose: () => void;
  onCreated: (bot: OttoBotWithSecret) => void;
  authHeader?: string;
}): JSX.Element {
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [persona, setPersona] = useState("chat");
  const [hostOrigins, setHostOrigins] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: "1.125rem", fontWeight: 600 }}>New Otto bot</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          sx={{ mt: 0.5, display: "grid", gap: 1.5 }}
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setErr(null);
            try {
              const bot = await ottoBotsApi.create({
                slug, display_name: displayName, persona,
                host_origins: hostOrigins.split(/\s*,\s*/).filter(Boolean),
              } as Partial<OttoBot>, authHeader);
              onCreated(bot);
            } catch (e) {
              setErr(e instanceof Error ? e.message : String(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          <TextField
            label="Slug (lowercase, dash-separated)"
            type="text"
            value={slug}
            required
            fullWidth
            inputProps={{ minLength: 3, pattern: "[a-z][a-z0-9-]+[a-z0-9]" }}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="webcast"
          />
          <TextField
            label="Display name"
            type="text"
            value={displayName}
            required
            fullWidth
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Webcast Helper"
          />
          <TextField
            label="Persona"
            select
            value={persona}
            fullWidth
            onChange={(e) => setPersona(e.target.value)}
          >
            <MenuItem value="chat">chat — customer-only (use for customer-facing bots)</MenuItem>
            <MenuItem value="support">support — customer + staff (internal bots)</MenuItem>
            <MenuItem value="marketing">marketing</MenuItem>
            <MenuItem value="sales">sales</MenuItem>
            <MenuItem value="content">content</MenuItem>
            <MenuItem value="admin">admin — every audience (debug only)</MenuItem>
          </TextField>
          <TextField
            label="Host origins (comma-separated)"
            type="text"
            value={hostOrigins}
            fullWidth
            onChange={(e) => setHostOrigins(e.target.value)}
            placeholder="https://webcast.xopnetworks.com"
          />

          {err && <Typography sx={{ fontSize: 12, color: "error.main" }}>{err}</Typography>}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}>
            <Button type="button" variant="text" color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={busy}>
              {busy ? "Creating…" : "Create bot"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
